import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
    EditOutlined,
    FileDownloadOutlined,
    RefreshOutlined,
    SearchOutlined,
} from "@mui/icons-material";
import {
    Box,
    Chip,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
} from "@mui/material";
import {
    DataGrid,
    GridActionsCellItem,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarDensitySelector,
} from "@mui/x-data-grid";
import { useGetActiveOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { showToast } from "utils/reusableToast";
import OrgEditDrawer from "./OrgEditDrawer";
import Pagination from "pages/homeDashboard/components/Pagination/Pagination";

import "./EcrOpsOrgManagement.css";

const RECORDS_PER_PAGE = 8;

// ── CSV export helper ─────────────────────────────────────────────────────────

function exportToCsv(rows) {
    const headers = ["Name", "Type", "Service Owner", "Org Owners", "Approvers", "Countries", "Membership AD Group", "Admin AD Group"];
    const csvRows = [
        headers.join(","),
        ...rows.map((r) =>
            [
                `"${r.name || ""}"`,
                `"${r.org_type || ""}"`,
                `"${r.service_owner || ""}"`,
                `"${(r.owners_email || []).join("; ")}",`,
                `"${(r.approvers || []).join("; ")}",`,
                `"${(r.countries || []).join("; ")}",`,
                `"${r.membership_ad_group || ""}",`,
                `"${r.admin_ad_group || ""}",`,
            ].join(",")
        ),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ecr_organisations.csv";
    a.click();
    URL.revokeObjectURL(url);
}

function getOrgTypeStyles(value) {
    const t = (value || "").toLowerCase();
    if (t === "us") return { bg: "#e8f4fc", fg: "#0550ae", border: "#add8f5" };
    if (t === "global") return { bg: "#e6f9f1", fg: "#86BC25", border: "#a2e2c3" };
    return { bg: "#f3f4f6", fg: "#6b7280", border: "#d1d5db" };
}

// ── Custom toolbar ────────────────────────────────────────────────────────────

const CustomToolbar = ({ rows, onRefresh, isRefreshing }) => (
    <GridToolbarContainer sx={{ padding: "8px 12px", gap: "4px" }}>
        <GridToolbarColumnsButton
            sx={{ color: "#1a1a1a", fontSize: "12px" }}
        />
        <GridToolbarDensitySelector
            sx={{ color: "#1a1a1a", fontSize: "12px" }}
        />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh organisations">
            <span>
                <IconButton
                    size="small"
                    onClick={onRefresh}
                    disabled={isRefreshing}
                    sx={{ color: "#1a1a1a" }}
                >
                    <RefreshOutlined fontSize="small" />
                </IconButton>
            </span>
        </Tooltip>
        <Tooltip title="Export to CSV">
            <IconButton
                size="small"
                onClick={() => exportToCsv(rows)}
                sx={{ color: "#1a1a1a" }}
            >
                <FileDownloadOutlined fontSize="small" />
            </IconButton>
        </Tooltip>
    </GridToolbarContainer>
);

CustomToolbar.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    onRefresh: PropTypes.func.isRequired,
    isRefreshing: PropTypes.bool.isRequired,
};

// ── Main component ────────────────────────────────────────────────────────────

const EcrOpsOrgManagement = () => {
    const location = useLocation();
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState(null);

    // Falls back to the default (first) platform until the ECR Ops platform
    // selector (EcrOpsLayout) has resolved a selection.
    const { data: platformData } = useGetPlatformsQuery();
    const selectedPlatformId = useSelector((state) => state.ecrOpsPlatform?.selectedPlatformId);
    const platformId = selectedPlatformId || platformData?._id;

    const {
        data: orgListData,
        isLoading,
        isFetching,
        refetch,
    } = useGetActiveOrganizationsQuery({ platformId, includeSyncConfig: true }, { skip: !platformId });

    // Show toast from CompleteOrg redirect
    useEffect(() => {
        if (location.state?.successMessage) {
            showToast({
                title: "Success",
                description: location.state.successMessage,
                type: "success",
            });
            // Clear state so it doesn't re-trigger on back navigation
            globalThis.history.replaceState({}, "");
        }
    }, [location.state]);

    const rows = useMemo(() => {
        const orgs = orgListData || [];
        return orgs.map((org) => ({
            id: org._id,
            _id: org._id,
            name: org.name || "",
            org_type: org.org_type || "",
            service_owner: org.service_owner || "",
            owners: Array.isArray(org.owners) ? org.owners : [],
            owners_email: Array.isArray(org.owners_email) ? org.owners_email : [],
            approvers: Array.isArray(org.approvers) ? org.approvers : [],
            membership_ad_group: org.membership_ad_group || "",
            admin_ad_group: org.admin_ad_group || "",
            countries: Array.isArray(org.countries) ? org.countries : [],
            visible: org.visible ?? true,
            approval_required: org.approval_required ?? false,
        }));
    }, [orgListData]);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const filteredRows = useMemo(() => {
        if (!search.trim()) return rows;
        const q = search.toLowerCase();
        return rows.filter(
            (r) =>
                r.name.toLowerCase().includes(q) ||
                r.service_owner.toLowerCase().includes(q) ||
                r.org_type.toLowerCase().includes(q) ||
                (r.owners || []).some(owner => owner.toLowerCase().includes(q)) ||
                (r.owners_email || []).some(email => email.toLowerCase().includes(q)) ||
                r.membership_ad_group.toLowerCase().includes(q)
        );
    }, [rows, search]);

    // Pagination
    const nPages = Math.ceil(filteredRows.length / RECORDS_PER_PAGE);
    const indexOfLastRecord = currentPage * RECORDS_PER_PAGE;
    const indexOfFirstRecord = indexOfLastRecord - RECORDS_PER_PAGE;
    const currentRecords = filteredRows.slice(indexOfFirstRecord, indexOfLastRecord);

    const handleOpenDrawer = useCallback(
        (id) => () => {
            const fullOrg = (orgListData || []).find((o) => o._id === id);
            console.log("Opening drawer for org:", fullOrg);
            if (fullOrg) {
                setSelectedOrg(fullOrg);
                setDrawerOpen(true);
            }
        },
        [orgListData]
    );

    const handleDrawerSaved = useCallback((message) => {
        showToast({
            title: "Organisation Updated",
            description: message,
            type: "success",
        });
        refetch();
    }, [refetch]);

    const columns = useMemo(
        () => [
            {
                field: "name",
                headerName: "Organisation Name",
                flex: 2,
                minWidth: 200,
                renderCell: (params) => (
                    <span style={{ fontSize: "13px", color: "#111827" }}>{params.value}</span>
                ),
            },
            {
                field: "owners_email",
                headerName: "Org Owners",
                flex: 2,
                minWidth: 180,
                renderCell: (params) => {
                    const list = params.value || [];
                    if (list.length === 0) return <span style={{ color: "#d1d5db" }}>—</span>;
                    if (list.length === 1) return <span style={{ fontSize: "13px", color: "#374151" }}>{list[0]}</span>;
                    return (
                        <Tooltip title={list.join(", ")} arrow>
                            <span style={{ fontSize: "13px", color: "#374151" }}>
                                {list[0]} <span style={{ color: "#0969da", fontWeight: 600 }}>+{list.length - 1} more</span>
                            </span>
                        </Tooltip>
                    );
                },
            },
            {
                field: "org_type",
                headerName: "Type",
                width: 100,
                renderCell: (params) => {
                    const { bg, fg, border } = getOrgTypeStyles(params.value);
                    return (
                        <Chip
                            label={params.value || "—"}
                            size="small"
                            sx={{
                                fontSize: "11px",
                                fontWeight: 600,
                                background: bg,
                                color: fg,
                                border: "1px solid",
                                borderColor: border,
                            }}
                        />
                    );
                },
            },
            {
                field: "membership_ad_group",
                headerName: "Membership AD Group",
                flex: 1.8,
                minWidth: 180,
                renderCell: (params) => (
                    <Tooltip title={params.value || ""} arrow>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", maxWidth: "100%", fontSize: "12px", color: "#6b7280" }}>
                            {params.value || <span style={{ color: "#d1d5db" }}>—</span>}
                        </span>
                    </Tooltip>
                ),
            },
            {
                field: "admin_ad_group",
                headerName: "Admin AD Group",
                flex: 1.8,
                minWidth: 180,
                renderCell: (params) => (
                    <Tooltip title={params.value || ""} arrow>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", maxWidth: "100%", fontSize: "12px", color: "#6b7280" }}>
                            {params.value || <span style={{ color: "#d1d5db" }}>—</span>}
                        </span>
                    </Tooltip>
                ),
            },
            {
                field: "service_owner",
                headerName: "Service Owner",
                flex: 1.5,
                minWidth: 160,
                renderCell: (params) => (
                    <span style={{ fontSize: "13px", color: "#374151" }}>{params.value || "—"}</span>
                ),
            },
            {
                field: "approvers",
                headerName: "Approvers",
                flex: 2,
                minWidth: 180,
                renderCell: (params) => {
                    const list = params.value || [];
                    if (list.length === 0) return <span style={{ color: "#d1d5db" }}>—</span>;
                    if (list.length === 1) return <span style={{ fontSize: "13px", color: "#374151" }}>{list[0]}</span>;
                    return (
                        <Tooltip title={list.join(", ")} arrow>
                            <span style={{ fontSize: "13px", color: "#374151" }}>
                                {list[0]} <span style={{ color: "#0969da", fontWeight: 600 }}>+{list.length - 1} more</span>
                            </span>
                        </Tooltip>
                    );
                },
            },
            {
                field: "countries",
                headerName: "Countries",
                width: 110,
                renderCell: (params) => {
                    const list = params.value || [];
                    return list.length === 0 ? (
                        <span style={{ color: "#d1d5db" }}>—</span>
                    ) : (
                        <Tooltip title={list.join(", ")} arrow>
                            <Chip
                                label={list.length === 1 ? list[0] : `${list.length} countries`}
                                size="small"
                                sx={{ fontSize: "11px", background: "#f3f4f6", color: "#374151" }}
                            />
                        </Tooltip>
                    );
                },
            },
            {
                field: "visible",
                headerName: "Visible",
                width: 80,
                renderCell: (params) => (
                    <Chip
                        label={params.value ? "Yes" : "No"}
                        size="small"
                        sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            background: params.value ? "#e6f9f1" : "#f3f4f6",
                            color: params.value ? "#86BC25" : "#6b7280",
                        }}
                    />
                ),
            },
            {
                field: "actions",
                type: "actions",
                headerName: "Edit",
                width: 70,
                getActions: ({ id }) => [
                    <GridActionsCellItem
                        key="edit"
                        icon={<EditOutlined sx={{ color: "#6b7280" }} />}
                        label="Edit"
                        onClick={handleOpenDrawer(id)}
                    />,
                ],
            },
        ],
        [handleOpenDrawer]
    );

    return (
        <div>
            {/* Page header */}
            <div className="ecr-ops-page-header">
                <p className="ecr-ops-page-header__eyebrow">ECR Ops Portal</p>
                <h1 className="ecr-ops-page-header__title">Organisation Management</h1>
                <p className="ecr-ops-page-header__sub">
                    View and edit all active organisations. Click the edit icon on any row to open the full edit panel.
                </p>
            </div>

            <div className="ecr-ops-content ecr-org-mgmt">
                {/* Filter bar */}
                <div className="ecr-org-mgmt__toolbar">
                    <span className="ecr-org-mgmt__count">
                        {filteredRows.length} {filteredRows.length === 1 ? "organisation" : "organisations"}
                    </span>
                    <TextField
                        size="small"
                        placeholder="Search by name, owner, or type…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlined sx={{ fontSize: 18, color: "#9ca3af" }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            width: 320,
                            marginLeft: "auto",
                            "& .MuiOutlinedInput-root": {
                                fontSize: "13px",
                                background: "#fff",
                                borderRadius: "8px",
                            },
                        }}
                    />
                </div>

                {/* Grid */}
                <div className="ecr-org-mgmt__grid-wrap">
                    <DataGrid
                        rows={currentRecords}
                        columns={columns}
                        loading={isLoading || isFetching}
                        rowHeight={64}
                        hideFooterPagination
                        hideFooterSelectedRowCount
                        slots={{
                            toolbar: CustomToolbar,
                        }}
                        slotProps={{
                            toolbar: { rows: filteredRows, onRefresh: refetch, isRefreshing: isFetching },
                        }}
                        sx={{
                            border: "none",
                            fontSize: "13px",
                            /* ── Header row (matches home dashboard) ── */
                            "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: "#F8FAFC",
                                borderBottom: "1px solid #DCE3EA",
                            },
                            "& .MuiDataGrid-columnHeader": {
                                backgroundColor: "#F8FAFC",
                            },
                            /* Title text must be targeted explicitly in MUI X v6 */
                            "& .MuiDataGrid-columnHeaderTitle": {
                                color: "#111827",
                                fontSize: "12px",
                                fontWeight: 700,
                                letterSpacing: "0.06em",
                            },
                            "& .MuiDataGrid-columnSeparator": {
                                color: "#E2E8F0",
                            },
                            "& .MuiDataGrid-sortIcon": {
                                color: "#64748b",
                            },
                            "& .MuiDataGrid-menuIconButton": {
                                color: "#64748b",
                            },
                            /* Fill the empty area to the right of the last column */
                            "& .MuiDataGrid-filler": {
                                background: "#F8FAFC",
                            },
                            "& .MuiDataGrid-scrollbarFiller": {
                                background: "#F8FAFC",
                            },
                            /* ── Rows ── */
                            "& .MuiDataGrid-row": {
                                borderBottom: "1px solid #E9EEF3",
                            },
                            "& .MuiDataGrid-row:hover": {
                                backgroundColor: "#F8FAFC !important",
                            },
                            "& .MuiDataGrid-row.Mui-selected": {
                                background: "#e6f6ee",
                            },
                            "& .MuiDataGrid-cell": {
                                borderBottom: "none",
                            },
                            "& .MuiDataGrid-footerContainer": {
                                borderTop: "1px solid #E2E8F0",
                                backgroundColor: "#FFFFFF",
                            },
                        }}
                    />
                </div>

                {/* Pagination */}
                <div style={{ marginTop: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: "13px", color: "#6b7280" }}>
                        Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, filteredRows.length)} of {filteredRows.length} organisation{filteredRows.length === 1 ? "" : "s"}
                    </div>
                    {nPages > 1 && (
                        <Pagination
                            nPages={nPages}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                        />
                    )}
                </div>
            </div>

            {/* Edit drawer */}
            <OrgEditDrawer
                open={drawerOpen}
                org={selectedOrg}
                onClose={() => setDrawerOpen(false)}
                onSaved={handleDrawerSaved}
            />
        </div>
    );
};

export default EcrOpsOrgManagement;
